import React from "react";
import { Box, Typography, Chip, useTheme } from "@mui/material";
import {
  Memory as MemoryIcon,
  Speed as CpuIcon,
  Dns as PodIcon,
} from "@mui/icons-material";

/**
 * Convert bytes to MB with formatting
 */
const formatMemory = (bytes) => {
  if (!bytes) return "0 MB";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${Math.round(mb)} MB`;
};

/**
 * Format CPU percentage
 */
const formatCpu = (cpu) => {
  if (!cpu) return "0%";
  return `${Math.round(cpu * 100) / 100}%`;
};

/**
 * Mini metric display component
 */
const MetricItem = ({ icon: Icon, value, label, color, isDarkMode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    <Icon
      sx={{
        fontSize: "0.9rem",
        color: color || (isDarkMode ? "#64748b" : "#94a3b8"),
      }}
    />
    <Typography
      variant="caption"
      sx={{
        fontWeight: 500,
        color: isDarkMode ? "#e2e8f0" : "#1e293b",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * Pod count badge
 */
const PodBadge = ({ count, isDarkMode }) => {
  const isScaledToZero = count === 0;

  return (
    <Chip
      size="small"
      icon={<PodIcon sx={{ fontSize: "0.85rem !important" }} />}
      label={isScaledToZero ? "Sleeping" : `${count} instance${count !== 1 ? "s" : ""}`}
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 500,
        background: isScaledToZero
          ? isDarkMode
            ? "rgba(100, 116, 139, 0.2)"
            : "rgba(100, 116, 139, 0.1)"
          : isDarkMode
          ? "rgba(34, 197, 94, 0.2)"
          : "rgba(34, 197, 94, 0.1)",
        color: isScaledToZero
          ? isDarkMode
            ? "#94a3b8"
            : "#64748b"
          : "#22c55e",
        border: "none",
        "& .MuiChip-icon": {
          color: "inherit",
          marginLeft: "6px",
        },
      }}
    />
  );
};

/**
 * Quick metrics display for app cards
 */
export function QuickMetrics({ runtime, compact = false }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const { pods = 0, cpu = 0, memory = 0 } = runtime || {};

  if (compact) {
    // Compact mode: single line
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <PodBadge count={pods} isDarkMode={isDarkMode} />
        {pods > 0 && (
          <>
            <MetricItem
              icon={CpuIcon}
              value={formatCpu(cpu)}
              isDarkMode={isDarkMode}
            />
            <MetricItem
              icon={MemoryIcon}
              value={formatMemory(memory)}
              isDarkMode={isDarkMode}
            />
          </>
        )}
      </Box>
    );
  }

  // Full mode: stacked
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <PodBadge count={pods} isDarkMode={isDarkMode} />
      {pods > 0 && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <MetricItem
            icon={CpuIcon}
            value={formatCpu(cpu)}
            label="CPU"
            isDarkMode={isDarkMode}
          />
          <MetricItem
            icon={MemoryIcon}
            value={formatMemory(memory)}
            label="Memory"
            isDarkMode={isDarkMode}
          />
        </Box>
      )}
    </Box>
  );
}

export default QuickMetrics;
